import classNames from 'classnames';
import { EditorMiddleware } from '@picsart/design-system/components/EditorMiddleware';
import { useEnvironmentScreen } from '@picsart/design-system/hooks/styling';

import useStyles from './styles';
import type { AppLayoutProps } from './types';

/**
 * Root shell for both viewports, built on `EditorMiddleware` — the DS template
 * that owns the root-entry environment (meta viewport, editor root styles,
 * pull-to-refresh and zoom suppression).
 *
 * Mobile used to use `EditorBlank`, which DS v13 replaced with `Blank`. `Blank`
 * spreads its `header` prop into a `PanelHeading` and drops `className`, so it
 * can no longer host this app's custom `<Header>`. The mobile branch keeps the
 * old `auto 1fr` header/body grid locally instead.
 *
 * The environment is set up by calling `useEnvironmentScreen` directly — the
 * external-mount path the DS documents — rather than by passing
 * `__isRootEntryElement`. That flag drives the same hook, but it also switches
 * the section's height from `100%` to `var(--editor-root-height)`, i.e. `100vh`.
 * `useEditorRootStyles` gives `html` that same `100vh` while DS `foundation.css`
 * pads `html` by the safe-area insets under `box-sizing: border-box`, so the
 * section would ask for the full viewport inside a content box already `100vh`
 * minus those insets and overflow the shell by the bottom one. Anything sized to
 * the shell instead — the full-screen `SearchScreen` overlay — then stops that
 * far short and leaves the screen underneath showing through the gap.
 *
 * The flags below reproduce what `__isRootEntryElement` passed; only the window
 * min-width differs by viewport, preserving `EditorBlank`'s behaviour of not
 * enforcing one on mobile. The rest default to `true`.
 *
 * The mobile grid drops to a single `1fr` row when `header` is omitted: with an
 * empty header slot the body is the grid's only child and auto-placement would
 * put it in the `auto` row, leaving its height content-driven. See
 * `mobileHeaderless` in `./styles`.
 */
const AppLayout = ({
  header,
  children,
  className,
  isMobile,
}: AppLayoutProps) => {
  const classes = useStyles();

  useEnvironmentScreen({ enableWindowMinWidth: !isMobile });

  if (isMobile) {
    return (
      <EditorMiddleware
        className={className}
        slot={
          <div
            className={classNames(classes.mobile, {
              [classes.mobileHeaderless]: !header,
            })}
          >
            {header}
            {children}
          </div>
        }
      />
    );
  }

  return (
    <EditorMiddleware
      className={className}
      slot={
        <div className={classes.desktop}>
          {header}
          {children}
        </div>
      }
    />
  );
};

export default AppLayout;
